/**
 * CookieProvider
 *
 * Wraps the application with:
 * 1. react-cookie's CookiesProvider for cookie read/write access.
 * 2. A ConsentContext that tracks granular consent preferences, exposes
 *    update helpers, and manages the Cookie Settings dialog open-state.
 *
 * Props:
 * @param appName - Used to look up the legacy consent cookie during migration.
 *                  Defaults to 'xanectech'.
 */
import * as React from 'react';
import { useCookies, CookiesProvider } from 'react-cookie';
import type { ConsentCategory, ConsentState } from '@xanectech/types';
import {
  writeConsent,
  clearConsent,
  checkConsentCategory,
  hasConsentDecision,
  migrateLegacyConsent,
  readConsent,
} from '@xanectech/lib';

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

export interface ConsentContextValue {
  /** Application identifier passed to CookieProvider (e.g. home, trueid-locker). */
  appName: string;
  /** Current consent state, or null if the user has not yet decided. */
  consentState: ConsentState | null;
  /** True once the user has made any consent decision. */
  hasDecided: boolean;
  /** Whether the Cookie Settings dialog is currently open. */
  isSettingsOpen: boolean;
  /** Open the Cookie Settings dialog. */
  openSettings: () => void;
  /** Close the Cookie Settings dialog. */
  closeSettings: () => void;
  /** Accept all categories (Essential + Functional + Analytics). */
  acceptAll: () => void;
  /** Accept only the Essential category. */
  rejectNonEssential: () => void;
  /**
   * Save a custom preference selection from the Cookie Settings dialog.
   * Essential is always true and cannot be changed.
   */
  savePreferences: (prefs: { functional: boolean; analytics: boolean }) => void;
  /** Check whether a specific category is currently consented to. */
  checkCategory: (category: ConsentCategory) => boolean;
}

const ConsentContext = React.createContext<ConsentContextValue | undefined>(
  undefined,
);

// ---------------------------------------------------------------------------
// Inner provider (needs to be inside CookiesProvider to access useCookies)
// ---------------------------------------------------------------------------

interface ConsentProviderProps extends React.PropsWithChildren {
  appName?: string;
}

const ConsentProvider: React.FC<ConsentProviderProps> = ({
  children,
  appName = 'xanectech',
}) => {
  const [consentState, setConsentState] = React.useState<ConsentState | null>(
    () => readConsent(appName),
  );
  const [isSettingsOpen, setIsSettingsOpen] = React.useState(false);

  const legacyCookieName = `user-consent-${appName.toLowerCase()}`;
  const [cookies] = useCookies([legacyCookieName, 'user-consent']);

  // On mount: migrate legacy consent if no structured decision exists yet.
  React.useEffect(() => {
    if (hasConsentDecision(appName)) return;

    const cookieValues = cookies as Record<string, unknown>;
    const legacyValue =
      (cookieValues[legacyCookieName] as string | undefined) ??
      (cookieValues['user-consent'] as string | undefined);

    if (legacyValue) {
      const migrated = migrateLegacyConsent(legacyValue, appName);
      if (migrated) {
        setConsentState(migrated);
      }
    }
  }, [appName, cookies, legacyCookieName]);

  const acceptAll = React.useCallback(() => {
    const state = writeConsent(
      { functional: true, analytics: true },
      'accept-all',
      appName,
    );
    setConsentState(state);
    setIsSettingsOpen(false);
  }, [appName]);

  const rejectNonEssential = React.useCallback(() => {
    const state = writeConsent(
      { functional: false, analytics: false },
      'reject-non-essential',
      appName,
    );
    setConsentState(state);
    setIsSettingsOpen(false);
  }, [appName]);

  const savePreferences = React.useCallback(
    (prefs: { functional: boolean; analytics: boolean }) => {
      const state = writeConsent(prefs, 'settings', appName);
      setConsentState(state);
      setIsSettingsOpen(false);
    },
    [appName],
  );

  const openSettings = React.useCallback(() => {
    setIsSettingsOpen(true);
  }, []);

  const closeSettings = React.useCallback(() => {
    setIsSettingsOpen(false);
  }, []);

  const checkCategory = React.useCallback(
    (category: ConsentCategory): boolean => {
      if (category === 'essential') return true;
      if (!consentState) return false;
      return consentState.preferences[category] ?? false;
    },
    [consentState],
  );

  const value = React.useMemo<ConsentContextValue>(
    () => ({
      appName,
      consentState,
      hasDecided: consentState !== null,
      isSettingsOpen,
      openSettings,
      closeSettings,
      acceptAll,
      rejectNonEssential,
      savePreferences,
      checkCategory,
    }),
    [
      appName,
      consentState,
      isSettingsOpen,
      openSettings,
      closeSettings,
      acceptAll,
      rejectNonEssential,
      savePreferences,
      checkCategory,
    ],
  );

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Public exports
// ---------------------------------------------------------------------------

export interface CookieProviderProps extends React.PropsWithChildren {
  appName?: string;
}

export const CookieProvider: React.FC<CookieProviderProps> = ({
  children,
  appName,
}) => (
  <CookiesProvider>
    <ConsentProvider appName={appName}>{children}</ConsentProvider>
  </CookiesProvider>
);

/**
 * Hook to access the consent context from any component inside CookieProvider.
 */
export const useCookieConsent = (): ConsentContextValue => {
  const ctx = React.useContext(ConsentContext);
  if (!ctx) {
    throw new Error('useCookieConsent must be used inside <CookieProvider>');
  }
  return ctx;
};

export { useCookies, clearConsent, checkConsentCategory };
